import React, { useState, useEffect, useRef, useCallback } from 'react'
import { connect } from 'react-redux'
import { useHistory, useRouteMatch } from 'react-router-dom'
import axios from 'axios'
import {
  Box, Typography, TextField, InputAdornment, Chip, Card, CardContent,
  CardMedia, CardActionArea, Button, Tabs, Tab, CircularProgress
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import SearchIcon from '@material-ui/icons/Search'
import AddIcon from '@material-ui/icons/Add'
import SettingsIcon from '@material-ui/icons/Settings'

const TRACK_ORDER = ['subcontractor_setup', 'sub_portal', 'sub_mobile_app', 'field_ops', 'herbicide_queue', 'site_measurement']

const TRACK_LABELS = {
  subcontractor_setup: 'Subcontractor Setup',
  sub_portal: 'Subcontractor Portal',
  sub_mobile_app: 'Mobile App',
  field_ops: 'Field Operations',
  herbicide_queue: 'Herbicide Queue',
  site_measurement: 'Site Measurement'
}

const AUDIENCE_COLORS = {
  office: '#1976d2',
  admin_only: '#c62828',
  sub_admin: '#388e3c',
  applicator: '#f57c00',
  estimator: '#7b1fa2'
}

const AUDIENCE_LABELS = {
  office: 'Office',
  admin_only: 'Admin Only',
  sub_admin: 'Sub Admin',
  applicator: 'Applicator',
  estimator: 'Estimator'
}

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    maxWidth: 1200,
    margin: '0 auto'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  searchBar: {
    width: 350,
    marginRight: theme.spacing(2)
  },
  tabs: {
    marginBottom: theme.spacing(3),
    borderBottom: '1px solid #e0e0e0'
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: theme.spacing(3)
  },
  card: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'box-shadow 0.2s',
    '&:hover': {
      boxShadow: theme.shadows[6]
    }
  },
  coverImage: {
    height: 160
  },
  trackBadge: {
    backgroundColor: '#e3f2fd',
    color: '#1565c0',
    fontWeight: 500,
    fontSize: '0.75rem',
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  audienceChip: {
    fontSize: '0.7rem',
    height: 22,
    marginRight: 4,
    marginBottom: 4
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary
  },
  adminButtons: {
    display: 'flex',
    gap: theme.spacing(1)
  }
}))

const ArticleBrowser = (props) => {
  const classes = useStyles()
  const history = useHistory()
  const { url } = useRouteMatch()
  const [articles, setArticles] = useState([])
  const [tracks, setTracks] = useState([])
  const [selectedTrack, setSelectedTrack] = useState('all')
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)

  const userType = (props.user?.userType || '').toLowerCase()
  const isAdmin = userType === 'admin' || userType === 'superadmin' || (props.perms && props.perms.length > 0)

  const debounceRef = useRef(null)

  const fetchArticles = useCallback(async (searchTerm) => {
    try {
      setLoading(true)
      const params = {}
      if (selectedTrack !== 'all') params.track = selectedTrack
      if (searchTerm) params.search = searchTerm
      const { data } = await axios.get('/api/knowledgebase/articles', { params })
      setArticles(data)
    } catch (e) {
      console.error('Error fetching articles:', e)
    } finally {
      setLoading(false)
    }
  }, [selectedTrack])

  useEffect(() => {
    fetchArticles(search)
    fetchTracks()
  }, [selectedTrack]) // eslint-disable-line

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      fetchArticles(search)
    }, 300)
    return () => clearTimeout(debounceRef.current)
  }, [search]) // eslint-disable-line

  const fetchTracks = async () => {
    try {
      const { data } = await axios.get('/api/knowledgebase/tracks')
      setTracks(data)
    } catch (e) {
      console.error('Error fetching tracks:', e)
    }
  }

  // Group articles by track for display
  const groupedArticles = articles.reduce((acc, article) => {
    const track = article.track
    if (!acc[track]) acc[track] = []
    acc[track].push(article)
    return acc
  }, {})

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography variant="h5" style={{ fontWeight: 600 }}>
          Knowledge Base
        </Typography>
        <Box display="flex" alignItems="center">
          <TextField
            className={classes.searchBar}
            variant="outlined"
            size="small"
            placeholder="Search articles..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              )
            }}
          />
          {isAdmin && (
            <Box className={classes.adminButtons}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<SettingsIcon />}
                onClick={() => history.push(`${url}/manage`)}
              >
                Manage
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                startIcon={<AddIcon />}
                onClick={() => history.push(`${url}/create`)}
              >
                New Article
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Track tabs */}
      <Tabs
        className={classes.tabs}
        value={selectedTrack}
        onChange={(e, val) => setSelectedTrack(val)}
        indicatorColor="primary"
        textColor="primary"
        variant="scrollable"
        scrollButtons="auto"
      >
        <Tab label={`All (${articles.length})`} value="all" />
        {tracks.map(t => (
          <Tab key={t.key} label={`${t.label} (${t.count})`} value={t.key} />
        ))}
      </Tabs>

      {/* Content */}
      {loading ? (
        <Box display="flex" justifyContent="center" py={8}>
          <CircularProgress />
        </Box>
      ) : articles.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6">No articles found</Typography>
          <Typography variant="body2">
            {isAdmin ? 'Create your first article to get started.' : 'Check back soon for new content.'}
          </Typography>
        </Box>
      ) : (
        TRACK_ORDER.filter(track => groupedArticles[track]).map(track => (
          <Box key={track} mb={4}>
            {selectedTrack === 'all' && (
              <Typography variant="h6" gutterBottom style={{ fontWeight: 500 }}>
                {TRACK_LABELS[track] || track}
              </Typography>
            )}
            <Box className={classes.grid}>
              {groupedArticles[track].map(article => (
                <Card key={article._id} className={classes.card}>
                  <CardActionArea onClick={() => history.push(`${url}/article/${article.slug}`)}>
                    {article.coverImage && (
                      <CardMedia
                        className={classes.coverImage}
                        image={article.coverImage}
                        title={article.title}
                      />
                    )}
                    <CardContent>
                      <Box mb={1}>
                        <Chip
                          className={classes.trackBadge}
                          label={TRACK_LABELS[article.track] || article.track}
                          size="small"
                        />
                        {article.status === 'draft' && (
                          <Chip label="Draft" size="small" style={{ backgroundColor: '#fff3e0', color: '#e65100' }} />
                        )}
                      </Box>
                      <Typography variant="subtitle1" style={{ fontWeight: 600, lineHeight: 1.3 }}>
                        {article.title}
                      </Typography>
                      {article.summary && (
                        <Typography variant="body2" color="textSecondary" style={{ marginTop: 4 }}>
                          {article.summary}
                        </Typography>
                      )}
                      <Box mt={1}>
                        {(article.audience || []).map(aud => (
                          <Chip
                            key={aud}
                            className={classes.audienceChip}
                            label={AUDIENCE_LABELS[aud] || aud}
                            size="small"
                            style={{ backgroundColor: AUDIENCE_COLORS[aud] || '#999', color: '#fff' }}
                          />
                        ))}
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              ))}
            </Box>
          </Box>
        ))
      )}
    </Box>
  )
}

const mapStateToProps = (state) => ({
  user: state.user,
  perms: state.perms,
  authenticated: state.authenticated
})

export default connect(mapStateToProps)(ArticleBrowser)
